'use client';
import { Button } from '@/shared/ui/Button';
import { clearAllBodyScrollLocks, disableBodyScroll } from 'body-scroll-lock-upgrade';
import { motion } from 'framer-motion';
import { useLenis } from 'lenis/react';
import Image from 'next/image';
import { FC, useEffect, useRef } from 'react';
import {
  cvaAside,
  cvaAsideBody,
  cvaAsideHeader,
  cvaAsideScroll,
  cvaClose,
  cvaCloseIcon,
  cvaDescr,
  cvaIcon,
  cvaIconWrapper,
  cvaItem,
  cvaList,
  cvaOverlay,
  cvaRoot,
  cvaTitle,
} from './FeaturesPopupStyles';
import IconClose from '/public/icons/close.svg';

interface Props {
  title: string;
  descr?: string;
  icon?: string;
  features: string[];
  onClose: () => void;
}

const FeaturesPopup: FC<Props> = ({ title, descr, icon, features, onClose }) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const rootLenis = useLenis();

  useEffect(() => {
    const lockPaddingValue = window.innerWidth - document.body.offsetWidth + 'px';

    if (scrollRef.current && rootLenis) {
      rootLenis?.stop();
      disableBodyScroll(scrollRef.current, {
        reserveScrollBarGap: true,
      });
      document.body.style.paddingRight = lockPaddingValue;
    }

    return () => {
      clearAllBodyScrollLocks();
      rootLenis?.start();
      document.body.style.paddingRight = '0';
    };
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{
        duration: 0.3,
        ease: 'easeOut',
      }}
      className={cvaRoot()}
    >
      <div className={cvaOverlay()} onClick={() => onClose()}></div>
      <div className={cvaAside()}>
        <Button
          className={cvaClose()}
          onClick={() => onClose()}
          rootWrapperClassName="absolute"
          color={'white'}
          hoverEffect={'highlight'}
        >
          <IconClose className={cvaCloseIcon()} />
        </Button>
        <div className={cvaAsideScroll()} ref={scrollRef} data-lenis-prevent>
          <div className={cvaAsideHeader()}>
            <div
              className={cvaTitle()}
              dangerouslySetInnerHTML={{
                __html: title,
              }}
            ></div>
            {descr ? (
              <div
                className={cvaDescr()}
                dangerouslySetInnerHTML={{
                  __html: descr,
                }}
              ></div>
            ) : null}
            {icon ? (
              <div className={cvaIconWrapper()}>
                <Image className={cvaIcon()} src={icon} fill alt="" />
              </div>
            ) : null}
          </div>
          <div className={cvaAsideBody()}>
            <ol className={cvaList()}>
              {features?.map((item, index) => (
                <li
                  className={cvaItem()}
                  key={index}
                  dangerouslySetInnerHTML={{
                    __html: item,
                  }}
                ></li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default FeaturesPopup;
